import { useState } from 'react';
import { Switch } from '@headlessui/react';
import { MicrophoneIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { ChatInputArea } from '@contentgrowth/llm-service/ui/react';

interface PreferencesSectionProps {
    isTranscriptionEnabled: boolean;
    onToggleTranscription: (enabled: boolean) => void;
    isSaving: boolean;
}

export default function PreferencesSection({
    isTranscriptionEnabled,
    onToggleTranscription,
    isSaving
}: PreferencesSectionProps) {
    const [isTesting, setIsTesting] = useState(false);
    const [testTranscript, setTestTranscript] = useState('');

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <span className="flex-grow flex flex-col">
                    <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <MicrophoneIcon className="h-4 w-4 text-gray-500" />
                        Enable High Quality Voice Transcription
                    </span>
                    <span className="text-sm text-gray-500">
                        Use server-side Whisper AI for more accurate voice-to-text transcription.
                        When disabled, uses browser's native speech recognition.
                    </span>
                </span>
                <Switch
                    checked={isTranscriptionEnabled}
                    onChange={onToggleTranscription}
                    disabled={isSaving}
                    className={`${isTranscriptionEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50`}
                >
                    <span className="sr-only">Use Whisper AI</span>
                    <span
                        aria-hidden="true"
                        className={`${isTranscriptionEnabled ? 'translate-x-5' : 'translate-x-0'
                            } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                </Switch>
            </div>

            {isTranscriptionEnabled && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                    {!isTesting ? (
                        <button
                            onClick={() => setIsTesting(true)}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
                        >
                            <BeakerIcon className="h-4 w-4" />
                            Test Transcription
                        </button>
                    ) : (
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-medium text-gray-900">Try it out</h4>
                                <button
                                    onClick={() => {
                                        setIsTesting(false);
                                        setTestTranscript('');
                                    }}
                                    className="text-xs text-gray-500 hover:text-gray-700"
                                >
                                    Close Test
                                </button>
                            </div>

                            <ChatInputArea
                                onSubmit={(text) => {
                                    setTestTranscript(''); // Clear on send
                                }}
                                isSending={false}
                                showInputForm={true}
                                currentTask={null}
                                placeholder="Tap microphone to test..."
                                value={testTranscript}
                                onChange={(e) => setTestTranscript(e.target.value)}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Tap the send button to clear the test text.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
